import {useCallback, useEffect, useState} from "react";
import {
  getAdminSymphonyaDashboard,
  sendAdminSymphonyaOrders,
  syncAdminSymphonyaProducts,
  testAdminSymphonyaConnection,
  type AdminSymphonyaConnectionTest,
  type AdminSymphonyaDashboard
} from "@zaaaz/shared";
import {adminToast} from "@/lib/adminToast";
import {AdminAlert, AdminLoading, AdminPageLayout} from "./ui";
import {SymphonyaDashboardPanel} from "./SymphonyaDashboardPanel";

export function IntegrationsView() {
  const [data, setData] = useState<AdminSymphonyaDashboard | null>(null);
  const [busy, setBusy] = useState<"" | "products" | "orders" | "test">("");
  const [err, setErr] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [connectionTest, setConnectionTest] = useState<AdminSymphonyaConnectionTest | null>(null);

  const load = useCallback(() => {
    getAdminSymphonyaDashboard()
      .then((d) => { setData(d); setErr(""); })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function run(job: "products" | "orders") {
    setBusy(job);
    try {
      if(job === "products") {
        const r = await syncAdminSymphonyaProducts();
        adminToast.success(r.durationMs != null ? `Product sync done (${r.durationMs}ms).` : "Product sync completed.");
      } else {
        const r = await sendAdminSymphonyaOrders();
        adminToast.success(
          r.pendingLeft != null
            ? `Order push finished · ${r.pendingLeft} still pending.`
            : "Order submission finished."
        );
      }
      load();
    } catch(e: unknown) {
      adminToast.error(e instanceof Error ? e.message : "Integration error");
      load();
    } finally {
      setBusy("");
    }
  }

  async function runConnectionTest() {
    setBusy("test");
    try {
      const r = await testAdminSymphonyaConnection();
      setConnectionTest(r);
      const create = r.tests.createOrder;
      if(create?.httpCode === 403 || /access denied/i.test(create?.bodyPreview || "")) {
        adminToast.error(`createOrder blocked (HTTP ${create?.httpCode ?? "?"}). Outbound IP: ${r.outboundIp ?? "unknown"}`);
      } else if(create?.ok || create?.parsed) {
        adminToast.success(`Connection OK from ${r.outboundIp ?? "server"} — createOrder returns JSON.`);
      } else {
        adminToast.error(`createOrder issue: HTTP ${create?.httpCode ?? "?"} — see results below.`);
      }
    } catch(e: unknown) {
      adminToast.error(e instanceof Error ? e.message : "Connection test failed");
    } finally {
      setBusy("");
    }
  }

  if(err && !loaded) return <AdminAlert variant="error">{err}</AdminAlert>;
  if(!loaded) return <AdminLoading lines={8} />;

  return (
    <AdminPageLayout
      title="Integrations"
      description="Symphonya ERP sync — product catalog and order push."
      breadcrumbs={[{label: "Dashboard", href: "/vadmin"}, {label: "Integrations"}]}
    >
      {err ? <AdminAlert variant="error">{err}</AdminAlert> : null}
      <SymphonyaDashboardPanel
        data={data}
        busy={busy}
        connectionTest={connectionTest}
        onSyncProducts={() => run("products")}
        onSendOrders={() => run("orders")}
        onTestConnection={runConnectionTest}
        onRefresh={load}
      />
    </AdminPageLayout>
  );
}
